import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import { QueryClient } from 'react-query';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { Locale, Stage } from 'app/generated/hygraph';
import type { VerificationMessageContent } from 'app/providers/AuthProvider/AuthProvider.types';
import Analytics, { Events } from 'app/services/Analytics';
import type { AppUser } from 'app/services/Firebase.types';
import { setEnv as setGuestCenterEnv } from 'app/services/GuestCenterService';
import { ContentEnv } from 'app/services/HygraphService';
import { getInitialLocale, I18nContent, initialContent, locales } from 'app/services/I18n';

const localeEntries = Object.entries(locales);

export enum VerificationState {
  Unverified = 'UNVERIFIED',
  Pending = 'PENDING',
  Verified = 'VERIFIED',
}

export enum AuthMode {
  Unset = 'UNSET',
  Token = 'TOKEN',
  VerifyLink = 'VERIFYLINK',
  SignInLink = 'SIGNINLINK',
  SignInGatherEmail = 'SIGNINGATHEREMAIL',
  TokenVerifyEmail = 'TOKENVERIFYEMAIL',
}

export type PurchasesReturnTarget =
  | { type: 'Menu' }
  | { type: 'Destinations' }
  | { type: 'Notifications' }
  | { type: 'Product'; productId: string }
  | { type: 'AttractionTickets'; productId: string }
  | { type: 'Attraction'; productId: string; attractionId: string };

export type AuthRedirectSource = 'Purchases';

export type StoreState = {
  locale: Locale;
  locales: [Locale, { label: string }][];
  orderAuthToken: Maybe<string>;
  email: Maybe<string>;
  user: Maybe<AppUser>;
  accessToken: Maybe<string>;
  emailReservingFor: Maybe<string>;
  verificationState: VerificationState;
  verificationProcessing: boolean;
  verificationMessage: Maybe<VerificationMessageContent>;
  authMode: AuthMode;
  productId: Maybe<string>;
  entitlementKey: Maybe<string>;
  lastTab: Maybe<'ProductStack' | 'AttractionTicketsStack'>;
  purchasesReturnTarget: PurchasesReturnTarget | null;
  authRedirectSource: AuthRedirectSource | null;
  hasCompletedWelcome: boolean;
  colorMode: 'light' | 'dark';
  content: I18nContent;
  env: Env;
  showContentKeys: boolean;
  cmsEnv: string;
  cmsStage: Stage;
  devTools: boolean;
  authCode: string;
  authCodeToken: Maybe<string>;
  nextReviewPrompt: string | null;
  nextAppDownloadPrompt: string | null;
  nextUpdateRecommendTime: string | null;
};

export type StoreActions = {
  setLocale: (locale: Locale) => void;
  setOrderAuthToken: (orderAuthToken: Maybe<string>) => void;
  setEmail: (email: Maybe<string>) => void;
  setUser: (user: Maybe<AppUser>) => void;
  setAccessToken: (accessToken: Maybe<string>) => void;
  setEmailReservingFor: (verificationEmail: Maybe<string>) => void;
  setVerificationState: (state: VerificationState) => void;
  setVerificationMessage: (verificationMessage: Maybe<VerificationMessageContent>) => void;
  setAuthMode: (state: AuthMode) => void;
  setProduct: (productId: Maybe<string>) => void;
  setEntitlementKey: (entitlementKey: Maybe<string>) => void;
  setLastTab: (lastTab: Maybe<'ProductStack' | 'AttractionTicketsStack'>) => void;
  setPurchasesReturnTarget: (purchasesReturnTarget: PurchasesReturnTarget | null) => void;
  clearPurchasesReturnTarget: () => void;
  setAuthRedirectSource: (authRedirectSource: AuthRedirectSource | null) => void;
  clearAuthRedirectSource: () => void;
  setColorMode: (colorMode: StoreState['colorMode']) => void;
  completeWelcome: () => void;
  setEnv: (env: Env, queryClient: QueryClient) => void;
  setVerificationProcessing: (isProcessing: boolean) => void;
  setShowContentKeys: (showContentKeys: boolean) => void;
  resetVerificationState: (email?: string) => void;
  setCMSEnv: (url: string) => void;
  setCMSStage: (stage: Stage) => void;
  setDevTools: (devTools: boolean) => void;
  emailVerificationPending: ({
    email,
    authCodeToken,
  }: {
    email: string;
    authCodeToken: string;
  }) => void;
  setAuthCode: (signInCode: string) => void;
  setUserVerified: () => void;
  setNextReviewPrompt: (date: string | null) => void;
  setNextAppDownloadPrompt: (date: string | null) => void;
  setNextUpdateRecommendTime: (time: string | null) => void;
  reset: () => void;
};

export type Store = StoreState & StoreActions;

export enum Env {
  DevFeature = 'devfeature',
  Dev = 'dev',
  Uat = 'uat',
  Integration = 'integration',
  Demo = 'demo',
  Staging = 'staging',
  ProdTesting = 'prodtesting',
  Production = 'production',
}

function createInitialState(): StoreState {
  return {
    // preferences
    locale: getInitialLocale(),
    locales: localeEntries,
    colorMode: 'light' as StoreState['colorMode'],
    cmsEnv: ContentEnv.Master,
    cmsStage: Stage.Published,
    devTools: false,
    showContentKeys: false,

    // auth
    verificationState: VerificationState.Unverified,
    verificationProcessing: false,
    verificationMessage: null,
    accessToken: null,
    orderAuthToken: null,
    authCode: '',
    authCodeToken: null,
    authMode: AuthMode.Unset,

    // user
    email: null,
    user: null,
    emailReservingFor: null,
    hasCompletedWelcome: false,

    // guest context
    productId: null,
    entitlementKey: null,
    lastTab: null,
    purchasesReturnTarget: null,
    authRedirectSource: null,

    // general
    content: initialContent,
    env: (Constants.expoConfig?.extra?.environment as Env) ?? Env.Dev,
    nextReviewPrompt: null,
    nextAppDownloadPrompt: null,
    nextUpdateRecommendTime: null,
  };
}

const useStore = create<Store>()(
  persist(
    (set, get) => ({
      ...createInitialState(),

      setEnv: async (env, queryClient) => {
        set({ env });
        setGuestCenterEnv(env);
        await Promise.all([queryClient.cancelQueries(), queryClient.cancelMutations()]);
        await queryClient.invalidateQueries();
      },
      setLocale: (locale) => set({ locale }),
      setOrderAuthToken: (orderAuthToken) => set({ orderAuthToken }),
      setEmail: (email) => set({ email }),
      setUser: (user) => set({ user }),
      setAccessToken: (accessToken) => set({ accessToken }),
      setEmailReservingFor: (verificationEmail) => set({ emailReservingFor: verificationEmail }),
      setVerificationState: (verificationState) => {
        if (verificationState === VerificationState.Verified) {
          Analytics.trackEvent(Events.AuthEmailVerified);
        }
        set({ verificationState });
      },
      setVerificationMessage: (verificationMessage) => set({ verificationMessage }),
      setAuthMode: (authMode) => set({ authMode }),
      setProduct: (productId) => set({ productId }),
      setLastTab: (lastTab) => set({ lastTab }),
      setPurchasesReturnTarget: (purchasesReturnTarget) => set({ purchasesReturnTarget }),
      clearPurchasesReturnTarget: () => set({ purchasesReturnTarget: null }),
      setAuthRedirectSource: (authRedirectSource) => set({ authRedirectSource }),
      clearAuthRedirectSource: () => set({ authRedirectSource: null }),
      completeWelcome: () => set({ hasCompletedWelcome: true }),
      setColorMode: (colorMode) => set({ colorMode }),
      setEntitlementKey: (entitlementKey) => set({ entitlementKey }),
      setVerificationProcessing: (verificationProceesing) =>
        set({ verificationProcessing: verificationProceesing }),
      setShowContentKeys: (showContentKeys) => set({ showContentKeys }),
      setNextReviewPrompt: (appReviewPrompt) => set({ nextReviewPrompt: appReviewPrompt }),
      setNextAppDownloadPrompt: (appDownloadPrompt) =>
        set({ nextAppDownloadPrompt: appDownloadPrompt }),
      setNextUpdateRecommendTime: (nextUpdateRecommendTime) => set({ nextUpdateRecommendTime }),
      resetVerificationState: (email) => {
        set({
          accessToken: null,
          authMode: AuthMode.Unset,
          email: email ?? null,
          emailReservingFor: null,
          user: null,
          verificationProcessing: get().verificationProcessing || false,
          verificationState: VerificationState.Unverified,
          authCode: '',
          authRedirectSource: get().authRedirectSource,
        });
      },

      emailVerificationPending: ({ email, authCodeToken }) => {
        set({
          email,
          authCodeToken,
          verificationState: VerificationState.Pending,
        });
      },

      setAuthCode: (authCode) => set({ authCode }),
      setUserVerified: () =>
        set({
          verificationState: VerificationState.Verified,
          authCode: '',
          authCodeToken: null,
        }),

      setCMSEnv: (cmsEnv) => set({ cmsEnv }),
      setCMSStage: (cmsStage) => set({ cmsStage }),

      setDevTools: (devTools) => set({ devTools }),

      reset: () =>
        set({
          ...createInitialState(),
          devTools: get().devTools,
        }),
    }),
    {
      name: 'GLOBAL_STORE',
      partialize: (state) => ({
        ...state,
        purchasesReturnTarget: null,
      }),
      storage: createJSONStorage(() => AsyncStorage),
    }
  )
);

export default useStore;

// TODO find example of how to type this
export const useStorePersist: {
  onHydrate: (cb: () => void) => () => void;
  onFinishHydration: (cb: () => void) => () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} = (useStore as typeof useStore & { persist: any }).persist;
